import React, { useId } from 'react';
import './ui.css';

// Componentes del design system de QuantumNodo (window.QN en el canvas de diseño).

function cx(...classes) {
    return classes.filter(Boolean).join(' ');
}

export function Button({ variant = 'ghost', size = 'md', block, className, type = 'button', children, ...rest }) {
    return (
        <button
            type={type}
            className={cx('qn-btn', `qn-btn-${variant}`, `qn-btn-${size}`, block && 'qn-btn-block', className)}
            {...rest}
        >
            {children}
        </button>
    );
}

const TONES = ['ok', 'warn', 'danger', 'neutral'];

export function StatusPill({ tone = 'neutral', className, children, ...rest }) {
    const t = TONES.includes(tone) ? tone : 'neutral';
    return (
        <span className={cx('qn-pill', `qn-pill-${t}`, className)} {...rest}>
            <span className="qn-pill-dot" aria-hidden="true" />
            {children}
        </span>
    );
}

export function Stat({ value, unit, label, hint, className }) {
    return (
        <div className={cx('qn-stat', className)}>
            <div className="qn-stat-value">
                {value}
                {unit && <span className="qn-stat-unit">{unit}</span>}
            </div>
            <div className="qn-stat-label">{label}</div>
            {hint && <div className="qn-stat-hint">{hint}</div>}
        </div>
    );
}

export function Progress({ value = 0, max = 100, label, showValue = true, className }) {
    const safeMax = max > 0 ? max : 1;
    const v = Math.max(0, Math.min(value, safeMax));
    const pct = Math.round((v / safeMax) * 100);
    return (
        <div className={cx('qn-progress', className)}>
            <div className="qn-progress-head">
                <span>{label}</span>
                {showValue && <span className="qn-progress-value">{v} / {max}</span>}
            </div>
            <div
                className="qn-progress-track"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={max}
                aria-valuenow={v}
                aria-label={label || 'Progreso'}
            >
                <div className="qn-progress-fill" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

export function Field({ label, hint, error, className, id, ...rest }) {
    const autoId = useId();
    const fieldId = id || autoId;
    return (
        <div className={cx('qn-field', error && 'qn-field-error', className)}>
            <label className="qn-field-label" htmlFor={fieldId}>{label}</label>
            <input className="qn-input" id={fieldId} aria-invalid={error ? 'true' : undefined} {...rest} />
            {(error || hint) && <span className="qn-field-hint">{error || hint}</span>}
        </div>
    );
}

export function Card({ title, meta, actions, featured, className, children, ...rest }) {
    return (
        <section className={cx('qn-card', featured && 'qn-card-featured', className)} {...rest}>
            {(title || meta || actions) && (
                <header className="qn-card-head">
                    <div className="qn-card-titles">
                        {title && <h2 className="qn-card-title">{title}</h2>}
                        {meta && <span className="qn-card-meta">{meta}</span>}
                    </div>
                    {actions && <div className="qn-card-actions">{actions}</div>}
                </header>
            )}
            {children}
        </section>
    );
}

// Íconos de trazo (sin emoji), heredan el color del texto.
const PATHS = {
    grid: <><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></>,
    file: <><path d="M6 2h9l5 5v15H6z" /><path d="M14 2v6h6" /><path d="M9 13h6M9 17h4" /></>,
    download: <><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M4 21h16" /></>,
    shield: <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />,
    users: <><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20c.8-3.5 3.3-5.5 6.5-5.5s5.7 2 6.5 5.5" /><path d="M16 4.5a3.5 3.5 0 010 7M18 14.8c1.8.8 3 2.5 3.5 5.2" /></>,
    link: <><path d="M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1" /><path d="M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></>,
    chevron: <path d="M9 6l6 6-6 6" />,
    alert: <><path d="M12 3l9.5 17h-19z" /><path d="M12 10v4M12 17.5v.01" /></>,
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 8v.01M11 12h1v5h1" /></>,
    lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></>,
    menu: <path d="M4 7h16M4 12h16M4 17h16" />,
    close: <path d="M6 6l12 12M18 6L6 18" />,
    check: <path d="M8.5 12.5l2.5 2.5 4.5-5" />,
    bang: <path d="M12 8.5v4M12 15.5v.01" />,
};

export function Icon({ name, size = 18, className, style }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            className={className}
            style={style}
        >
            {PATHS[name]}
        </svg>
    );
}
